import {
    createRouter,
    createWebHistory,
    NavigationGuardNext,
    RouteLocationNormalized,
    RouteRecordRaw
} from "vue-router";
import {
    unitCheckContext,
    contractContext,
    userContext
} from "@/store";
import keycloak from "@/auth/keycloak";

const routes: Array<RouteRecordRaw> = [
    {
        path: "/",
        name: "Buildings",
        meta: {
            title: "Buildings"
        },
        component: () => import("../views/Buildings.vue"),
    },
    {
        path: "/contract",
        name: "Contract",
        meta: {
            title: "Contract",
            backRouteName: "ContractSearch"
        },
        component: () => import("../views/ContractInfo.vue"),
    },
    {
        path: "/contract/search",
        name: "ContractSearch",
        meta: {
            title: "Search contract",
            backRouteName: "Dashboard"
        },
        component: () => import("../views/ContractSearch.vue"),
    },
    {
        path: "/dashboard",
        name: "Dashboard",
        meta: {
            title: "Dashboard",
            backRouteName: "Buildings"
        },
        component: () => import("../views/Dashboard.vue"),
    },
    {
        path: "/access-control/devices",
        name: "AccessControlDevices",
        meta: {
            title: "Access Control",
            backRouteName: "Dashboard"
        },
        component: () => import("../views/AccessControlDevices.vue"),
    },
    {
        path: "/access-control/device",
        name: "AccessControlDevice",
        meta: {
            title: "Device",
            backRouteName: "AccessControlDevices"
        },
        component: () => import("../views/AccessControlDevice.vue"),
    },
    {
        path: "/access-control/cards",
        name: "AccessControlCards",
        meta: {
            title: "Cards",
            backRouteName: "AccessControlDevice"
        },
        component: () => import("../views/AccessControlCards.vue"),
    },
    {
        path: "/access-control/card",
        name: "AccessControlCard",
        meta: {
            title: "Card",
            backRouteName: "AccessControlCards"
        },
        component: () => import("../views/AccessControlCard.vue"),
    },
    {
        path: "/access-control/card/edit",
        name: "AccessControlCardEdit",
        meta: {
            title: "Edit Card",
            backRouteName: "AccessControlCard"
        },
        component: () => import("../views/AccessControlCardForm.vue"),
    },
    {
        path: "/access-control/card/create",
        name: "AccessControlCardCreate",
        meta: {
            title: "Create Card",
            backRouteName: "AccessControlCards"
        },
        component: () => import("../views/AccessControlCardForm.vue"),
    },
    {
        path: "/access-control/doors",
        name: "AccessControlDoors",
        meta: {
            title: "Doors",
            backRouteName: "AccessControlDevice"
        },
        component: () => import("../views/AccessControlDoors.vue"),
    },
    {
        path: "/access-control/door",
        name: "AccessControlDoor",
        meta: {
            title: "Door",
            backRouteName: "AccessControlDoors"
        },
        component: () => import("../views/AccessControlDoor.vue"),
    },
    {
        path: "/access-control/events",
        name: "AccessControlEvents",
        meta: {
            title: "Events",
            backRouteName: "AccessControlDevice"
        },
        component: () => import("../views/AccessControlEvents.vue"),
    },
    {
        path: "/jobs",
        name: "Jobs",
        meta: {
            title: "Jobs",
            backRouteName: "Dashboard"
        },
        component: () => import("../views/Jobs.vue"),
    },
    {
        path: "/job/create",
        name: "JobCreate",
        meta: {
            title: "Create Job",
            backRouteName: "JobNew"
        },
        component: () => import("../views/JobCreate.vue"),
    },
    {
        path: "/unit/issue/create",
        name: "KnownIssueCreate",
        meta: {
            title: "Create Known Issue",
            backRouteName: "Unit"
        },
        component: () => import("../views/KnownIssueCreate.vue"),
    },
    {
        path: "/job/new",
        name: "JobNew",
        meta: {
            title: "New Job",
            backRouteName: "Unit"
        },
        component: () => import("../views/JobNew.vue"),
    },
    {
        path: "/login",
        name: "Login",
        meta: {
            title: "Login",
            public: true
        },
        component: () => import("../views/Login.vue"),
    },
    {
        path: "/meters",
        name: "Meters",
        meta: {
            title: "Meters",
            backRouteName: "Unit"
        },
        component: () => import("../views/Meters.vue"),
    },
    {
        path: "/qr-code-server",
        name: "QrCodeServer",
        meta: {
            title: "QR-Code Server",
            backRouteName: "Dashboard"
        },
        component: () => import("../views/QrCodeServer.vue"),
    },
    {
        path: "/qr-code-server/codes",
        name: "QrCodeServerCodes",
        meta: {
            title: "Codes",
            backRouteName: "QrCodeServer"
        },
        component: () => import("../views/QrCodeServerCodes.vue"),
    },
    {
        path: "/qr-code-server/logs",
        name: "QrCodeServerLogs",
        meta: {
            title: "Logs",
            backRouteName: "QrCodeServer"
        },
        component: () => import("../views/QrCodeServerLogs.vue"),
    },
    {
        path: "/units",
        name: "Units",
        meta: {
            title: "Units",
            backRouteName: "Dashboard"
        },
        component: () => import("../views/Units.vue"),
    },
    {
        path: "/unit",
        name: "Unit",
        meta: {
            title: "Unit",
            backRouteName: "Units"
        },
        component: () => import("../views/Unit.vue"),
    },
    {
        path: "/unit/check",
        name: "UnitCheck",
        meta: {
            title: "Unit check",
            backRouteName: "Unit"
        },
        component: () => import("../views/UnitCheck.vue"),
    },
    {
        path: "/utility-counters",
        name: "UtilityCounters",
        meta: {
            title: "Utility counters",
            backRouteName: "Dashboard"
        },
        component: () => import("../views/UtilityCounterReadings.vue"),
    },
    {
        path: "/warehouse-shop",
        name: "WarehouseShop",
        meta: {
            title: "Warehouse shop",
            backRouteName: "Dashboard",
            requiredAbility: "warehouse-shop"
        },
        component: () => import("../views/WarehouseShop.vue"),
    },
    {
        path: "/warehouse-admin",
        name: "WarehouseAdmin",
        meta: {
            title: "Warehouse admin",
            backRouteName: "Dashboard",
            requiredAbility: "warehouse-admin"
        },
        component: () => import("../views/WarehouseAdmin.vue"),
    },
    {
        path: "/warehouse-admin/products",
        name: "WarehouseAdminProducts",
        meta: {
            title: "Manage products",
            backRouteName: "WarehouseAdmin",
            requiredAbility: "warehouse-admin"
        },
        component: () => import("../views/WarehouseAdminProducts.vue"),
    },
    {
        path: "/warehouse-admin/products/create",
        name: "WarehouseAdminProductCreate",
        meta: {
            title: "Add product",
            backRouteName: "WarehouseAdminProducts",
            requiredAbility: "warehouse-admin"
        },
        component: () => import("../views/WarehouseAdminProduct.vue"),
    },
    {
        path: "/warehouse-admin/products/:productId",
        name: "WarehouseAdminProduct",
        meta: {
            title: "Manage product",
            backRouteName: "WarehouseAdminProducts",
            requiredAbility: "warehouse-admin"
        },
        component: () => import("../views/WarehouseAdminProduct.vue"),
    },
    {
        path: "/warehouse-admin/orders",
        name: "WarehouseAdminOrders",
        meta: {
            title: "Manage active orders",
            backRouteName: "WarehouseAdmin",
            requiredAbility: "warehouse-admin"
        },
        component: () => import("../views/WarehouseAdminOrders.vue"),
    },
    {
        path: "/warehouse-admin/orders/:orderId",
        name: "WarehouseAdminOrder",
        meta: {
            title: "Manage order",
            backRouteName: "WarehouseAdminOrders",
            requiredAbility: "warehouse-admin"
        },
        component: () => import("../views/WarehouseAdminOrder.vue"),
    },
    {
        path: "/warehouse-admin/stock",
        name: "WarehouseAdminStock",
        meta: {
            title: "Manage stock",
            backRouteName: "WarehouseAdmin",
            requiredAbility: "warehouse-admin"
        },
        component: () => import("../views/WarehouseAdminStock.vue"),
    },
    {
        path: "/warehouse-admin/restocks",
        name: "WarehouseAdminRestocks",
        meta: {
            title: "Restock",
            backRouteName: "WarehouseAdmin",
            requiredAbility: "warehouse-admin"
        },
        component: () => import("../views/WarehouseAdminRestocks.vue"),
    },
    {
        path: "/warehouse-admin/retours",
        name: "WarehouseAdminRetours",
        meta: {
            title: "Retours",
            backRouteName: "WarehouseAdmin",
            requiredAbility: "warehouse-admin"
        },
        component: () => import("../views/WarehouseAdminRetours.vue"),
    },
    {
        path: "/warehouse-admin/restock-history",
        name: "WarehouseAdminRestockHistory",
        meta: {
            title: "Restock history",
            backRouteName: "WarehouseAdmin",
            requiredAbility: "warehouse-admin"
        },
        component: () => import("../views/WarehouseAdminRestockHistory.vue"),
    },
    {
        path: "/warehouse-admin/suppliers",
        name: "WarehouseAdminSuppliers",
        meta: {
            title: "Manage suppliers",
            backRouteName: "WarehouseAdmin",
            requiredAbility: "warehouse-admin"
        },
        component: () => import("../views/WarehouseAdminSuppliers.vue"),
    },
    {
        path: "/warehouse-admin/resupply-tasks",
        name: "WarehouseAdminResupplyTasks",
        meta: {
            title: "Resupply history",
            backRouteName: "WarehouseAdmin",
            requiredAbility: "warehouse-admin"
        },
        component: () => import("../views/WarehouseAdminResupplyTasks.vue"),
    },
    {
        path: "/warehouse-admin/reports",
        name: "WarehouseAdminReports",
        meta: {
            title: "Reports",
            backRouteName: "WarehouseAdmin",
            requiredAbility: "warehouse-admin"
        },
        component: () => import("../views/WarehouseAdminReports.vue"),
    },
];

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes
});

let routeGuardReadyPromise: Promise<void> = Promise.resolve();

export function setRouteGuardReadyPromise(promise: Promise<void>): void {
    routeGuardReadyPromise = promise;
}

function guard(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): void {
    const requiredAbility = to.meta.requiredAbility as string|undefined;

    if (requiredAbility && !userContext.getters.hasAbility(requiredAbility)) {
        next({name: "Dashboard"});
    } else if (to.name === "UnitCheck" && !unitCheckContext.state.selectedUnitCheckId) {
        next({name: "Unit"});
    } else if (to.name === "Contract" && !contractContext.state.selectedContractInfoId) {
        next({name: "ContractSearch"});
    } else {
        next();
    }
}

router.beforeEach(async (to, from, next) => {
    const isPublic = Boolean(to.meta.public);

    const hash = to.hash || "";

    const looksLikeKeycloakCallback =
        hash.startsWith("#state=") ||
        hash.includes("session_state=") ||
        hash.includes("iss=") ||
        hash.includes("code=");

    if (looksLikeKeycloakCallback) {
        next({
            path: to.path,
            query: to.query,
            hash: "",
            replace: true,
        });
        return;
    }

    if (!isPublic && !keycloak.authenticated) {
        await keycloak.login({
            redirectUri: window.location.origin + to.fullPath
        });
        return;
    }

    await routeGuardReadyPromise;

    guard(to, from, next);
});

export default router;
